import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { buildClass } from "@utils/tag";
import { useDispatch, useSelector } from "noval";
import useTranslation from "next-translate/useTranslation";
import Notification from "@components/Notification";
import RenderTitle from "./RenderTitle";
import Link from "next/link";
import BurgerMenuIcon from "public/images/global/BurgerMenuIcon";
import Slide from "@components/Home/SlideComponent";
import NavBar from "@components/Home/NavBar";
import { Icon } from "@utils/helper";
import { useEffect, useState } from "react";
import useNetworkStatus from "@components/status/useNetworkStatus";

const IqamaModal = dynamic(() => import("@components/modals/IqamaModal"), {
  ssr: false,
});

const PolicyVerificationModal = dynamic(
  () => import("@components/modals/PolicyVerificationModal"),
  { ssr: false }
);

const NoInternetConnection = dynamic(
  () => import("@components/status/NoInternetConnection"),
  {
    ssr: false,
  }
);

const ReminderModal = dynamic(
  () => import("@components/modals/ReminderModal"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const KycModal = dynamic(() => import("@components/modals/KycModal"), {
  ssr: true,
});

const DownloadAppModal = dynamic(
  () => import("@components/modals/DownloadAppModal"),
  {
    ssr: true,
  }
);

const AuthLayout = ({ children }) => {
  const { pathname } = useRouter();
  const { dispatch } = useDispatch();
  const { isOnline } = useNetworkStatus();
  const { lang } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [
    screen,
    kycOpen,
    iqamaAlert,
    reminderOpen,
    downloadAppOpen,
    policyVerificationOpen,
  ] = useSelector([
    "screen",
    "kycOpen",
    "iqamaAlert",
    "reminderOpen",
    "downloadAppOpen",
    "policyVerificationOpen",
  ]);

  useEffect(() => {
    setMounted(true);
    window.Intercom &&
      window.Intercom("update", {
        hide_default_launcher: true,
        ...(lang === "ar" ? { alignment: "left" } : { alignment: "right" }),
      });
  }, [lang]);

  return (
    <>
      <div
        className="flex min-h-screen w-full mx-auto"
        style={{
          backgroundSize: "auto",
          backgroundImage: "url(/images/background/back-login.webp) ",
          background: `url(/images/background/back-login.webp), linear-gradient(${
            lang === "en" ? "275.01deg" : "95.01deg"
          }, #05128B 0.06%, #2B64F5 105.54%, #5CAEFF 136.9%)`,
        }}
      >
        <Slide />
        <div className="flex flex-col w-full h-screen relative">
          {screen === "sm-phone" && (
            <div className="w-full h-[150px] flex justify-between items-center px-3">
              <Link href="/">
                <Icon
                  id="LogoWhite"
                  width="95px"
                  height="38px"
                  viewBox="0 0 173 64"
                />
              </Link>
              <div className="flex items-center gap-3">
                <Notification />
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    dispatch({ openDrawer: true });
                  }}
                >
                  <BurgerMenuIcon
                    height={25}
                    width={33}
                    itemHeight={4}
                    color="#fff"
                  />
                </div>
              </div>
            </div>
          )}
          <div
            className={buildClass(
              "flex justify-center bg-[#F8FBFF]",
              " sm:p-3 md:p-8 rounded-t-[30px] sm:rounded-none",
              "xl:rounded-s-[30px] w-full h-[100vh]",
              "overflow-y-hidden sm:overflow-y-scroll"
            )}
          >
            <div
              className={buildClass(
                "flex flex-col !gap-8 w-full pt-3 sm:pt-0",
                "max-w-[1100px] overflow-x-clip text-black",
                "h-full overflow-y-scroll sm:overflow-y-visible"
              )}
            >
              {screen !== "sm-phone" && (
                <div className="block">
                  <NavBar />
                </div>
              )}

              {!pathname.includes("home/[fund_id]") && (
                <div className="px-3 sm:hidden">
                  <RenderTitle />
                </div>
              )}
              {children}
              <div>
                {mounted && !isOnline ? <NoInternetConnection /> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {policyVerificationOpen ? (
        <PolicyVerificationModal
          isOpen={policyVerificationOpen}
          onClose={() => dispatch({ policyVerificationOpen: false })}
        />
      ) : null}
      {iqamaAlert ? (
        <IqamaModal
          isOpen={iqamaAlert}
          onClose={() => dispatch({ iqamaAlert: false })}
        />
      ) : null}
      {reminderOpen ? (
        <ReminderModal
          isOpen={reminderOpen}
          onClose={() => dispatch({ reminderOpen: false })}
        />
      ) : null}
      {kycOpen ? (
        <KycModal
          isOpen={kycOpen}
          onClose={() => dispatch({ kycOpen: false })}
        />
      ) : null}
      {downloadAppOpen ? (
        <DownloadAppModal
          isOpen={downloadAppOpen}
          onClose={() => dispatch({ downloadAppOpen: false })}
        />
      ) : null}
    </>
  );
};

export default AuthLayout;
